import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AccountMemberPicker, type AccountMember } from "@/components/account/AccountMemberPicker";
import { AdminAlert } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError, api } from "@/lib/api";

type Assignee = { id: number; user_id: number };

type AssignAssigneesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 被授权账户 ID（本账户），用于拉取内部成员 */
  granteeAccountId: string;
  authorizationId: number;
  /** 授权方企业名，用于弹窗说明 */
  grantorLabel: string;
  /** 保存成功后回调（如刷新左上角账户列表） */
  onSaved?: () => void;
};

export function AssignAssigneesDialog({
  open,
  onOpenChange,
  granteeAccountId,
  authorizationId,
  grantorLabel,
  onSaved,
}: AssignAssigneesDialogProps) {
  const [members, setMembers] = useState<AccountMember[]>([]);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.fetchForAccount<{ memberships: AccountMember[] }>(
        granteeAccountId,
        `/accounts/${granteeAccountId}/memberships`,
      ),
      api.fetch<{ assignees: Assignee[] }>(`/third_party_authorizations/${authorizationId}/assignees`),
    ])
      .then(([{ memberships }, { assignees: nextAssignees }]) => {
        if (cancelled) return;
        const internal = memberships.filter((item) => item.kind === "internal" && !item.disabled);
        setMembers(internal);
        setAssignees(nextAssignees);
        setSelectedIds(nextAssignees.map((item) => item.user_id));
      })
      .catch((caughtError) => {
        if (cancelled) return;
        setError(caughtError instanceof ApiError ? caughtError.message : "无法加载成员列表");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, granteeAccountId, authorizationId]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    const selectedSet = new Set(selectedIds);
    const initial = new Set(assignees.map((item) => item.user_id));
    const toAdd = selectedIds.filter((userId) => !initial.has(userId));
    const toRemove = assignees.filter((item) => !selectedSet.has(item.user_id));

    try {
      for (const userId of toAdd) {
        await api.fetch(`/third_party_authorizations/${authorizationId}/assignees`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
      }
      for (const assignee of toRemove) {
        await api.fetch(`/third_party_authorizations/${authorizationId}/assignees/${assignee.id}`, {
          method: "DELETE",
        });
      }
      toast.success(
        toAdd.length === 0 && toRemove.length === 0
          ? "未变更可进入人员"
          : `已更新可进入人员（+${toAdd.length} / −${toRemove.length}）`,
      );
      onSaved?.();
      onOpenChange(false);
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "保存失败，请稍后重试");
    } finally {
      setSaving(false);
    }
  }

  const selectedCount = selectedIds.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(85vh,640px)] flex-col gap-4 overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>分配可进入人员</DialogTitle>
          <DialogDescription>
            选择本账户内部成员，使其可进入「{grantorLabel}」。可搜索、多选。
          </DialogDescription>
        </DialogHeader>

        {error ? <AdminAlert>{error}</AdminAlert> : null}

        <AccountMemberPicker
          mode="multiple"
          members={members}
          value={selectedIds}
          onChange={setSelectedIds}
          loading={loading}
          disabled={saving}
          emptyText="暂无可用内部成员"
          className="min-h-0 flex-1"
        />

        <DialogFooter className="sm:justify-between">
          <p className="text-xs text-muted-foreground self-center">已选 {selectedCount} 人</p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              取消
            </Button>
            <Button type="button" onClick={() => void handleSave()} disabled={loading || saving}>
              {saving ? "保存中…" : "保存"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
